import IModelData from '../types/ModelData';
import {
  ModelSection,
  ModelSectionType,
  DEFAULT_SECTION_CONFIGS,
  createEmptySection,
} from '../types/ModelSection';

/**
 * SECTION_INPUT_ROOT_TYPES / SECTION_OUTPUT_ROOT_TYPES — entry-point types
 * for each section, split per kind because the input and output trees can
 * disagree on what counts as a root.
 *
 * Taxation is the case in point: on the input side the root is the
 * FISCALITY_PARENT container (whose stored type is `'FISCALITY'`,
 * lower-cased to `'fiscality'` when matching), and `country_fiscality` items
 * are its children. On the output side there is no FISCALITY_PARENT — output
 * trees root the section directly at `country_fiscality`.
 *
 * Mirror of api/model_scenarios/sections.py SECTION_INPUT_TYPES /
 * SECTION_OUTPUT_TYPES. Keep in sync.
 */
export const SECTION_INPUT_ROOT_TYPES: Record<ModelSectionType, string[]> = {
  [ModelSectionType.COMPANY]: ['company'],
  [ModelSectionType.ASSET]: ['company'],
  [ModelSectionType.EQUITY]: ['company'],
  [ModelSectionType.CORPORATE_TAXATION]: ['company'],
  [ModelSectionType.BANK_ACCOUNTS]: ['company'],
  [ModelSectionType.FINANCIAL_DEBT]: ['financial_debt'],
  [ModelSectionType.INDEXATION]: ['indexation_references'],
  [ModelSectionType.MACRO_ECONOMICS]: ['macroeconomics'],
  [ModelSectionType.TAXATION]: ['fiscality'],
};

export const SECTION_OUTPUT_ROOT_TYPES: Record<ModelSectionType, string[]> = {
  [ModelSectionType.COMPANY]: ['company'],
  [ModelSectionType.ASSET]: ['company'],
  [ModelSectionType.EQUITY]: ['company'],
  [ModelSectionType.CORPORATE_TAXATION]: ['company'],
  [ModelSectionType.BANK_ACCOUNTS]: ['company'],
  [ModelSectionType.FINANCIAL_DEBT]: ['financial_debt'],
  [ModelSectionType.INDEXATION]: ['indexation_references'],
  [ModelSectionType.MACRO_ECONOMICS]: ['macroeconomics'],
  [ModelSectionType.TAXATION]: ['country_fiscality'],
};

/**
 * Union of input + output roots — used by `typeMatchesSection` (section
 * membership is symmetric: a `country_fiscality` is part of Taxation whether
 * it lives on the input or the output side).
 *
 * Kept exported for the rare caller that doesn't know its kind, but new code
 * should reach for the kind-specific maps above.
 */
export const SECTION_ROOT_TYPES: Record<ModelSectionType, string[]> = Object
    .values(ModelSectionType)
    .reduce((acc, sectionType) => {
        acc[sectionType] = Array.from(new Set([
            ...SECTION_INPUT_ROOT_TYPES[sectionType],
            ...SECTION_OUTPUT_ROOT_TYPES[sectionType],
        ]));
        return acc;
    }, {} as Record<ModelSectionType, string[]>);

// Asset OPEX/REVENUES/QUANTITY variants (fixed/variable/recurring/timeserie/
// periodic/conditional/aggregated/constant) plus shared leaf helpers.
// Both input and output value forms are listed because outputs drop the
// underscore in some names (e.g. conditional_time_serie_opex -> conditional_timeserie_opex).
const ASSET_TYPES = new Set<string>([
  'company', 'asset',
  'capex', 'capex_parent',
  'opex', 'revenues', 'quantity',
  'aggregated_quantity', 'aggregated_revenues', 'aggregated_opex',
  'constant_quantity',
  'calendar_monthly', 'calendar_quarterly', 'calendar_semi_annual',
  'fixed', 'fixed_revenues', 'fixed_opex', 'fixed_quantity',
  'variable', 'variable_revenues', 'variable_opex', 'variable_quantity',
  'recurring', 'recurring_revenues', 'recurring_opex', 'recurring_quantity',
  'threshold',
  'time_serie', 'time_serie_revenues', 'time_serie_opex', 'time_serie_quantity',
  'timeserie', 'timeserie_revenues', 'timeserie_opex', 'timeserie_quantity',
  'direct_production_cost', 'overhead', 'general_expenses', 'general_expense',
  'local_taxes', 'total_local_taxes', 'taxes', 'total', 'total_capex', 'total_financial_fees',
  'conditional_opex', 'conditional_direct_production_cost',
  'conditional_variable_opex', 'conditional_recurring_opex',
  'conditional_overhead', 'conditional_general_expenses',
  'conditional_time_serie_opex', 'conditional_timeserie_opex',
  'conditional_periodic_opex',
  'conditional_revenues', 'conditional_fixed_revenues',
  'conditional_variable_revenues', 'conditional_recurring_revenues',
  'conditional_time_serie_revenues', 'conditional_timeserie_revenues',
  'conditional_quantity', 'conditional_fixed_quantity',
  'conditional_constant_quantity', 'conditional_variable_quantity',
  'conditional_recurring_quantity',
  'conditional_time_serie_quantity', 'conditional_timeserie_quantity',
  'conditional_aggregated_quantity',
  'conditional_index',
  'periodic_opex',
]);

const EQUITY_TYPES = new Set<string>([
  'company',
  'equity', 'equity_parent', 'valuation_equity',
  'valuation_debt', 'valuation_project',
  'shl_specific_profile',
  'common_share', 'shareholder_loan',
  'sub_company_common_share', 'sub_company_shareholder_loan',
  'dividends',
]);

const CORPORATE_TAXATION_TYPES = new Set<string>([
  'company',
  // Input C_FISCALITY subtree
  'company_fiscality',
  'company_value_added_tax',
  'company_property_tax_parent',
  'company_property_tax',
  'company_corporate_income_tax',
  'company_operation_income_taxable',
  'company_operation_expense_deductible',
  'company_financial_income_taxable',
  'company_shl_rate_taxable',
  'company_dividends_taxable',
  'company_financial_disposal_gain_taxable',
  'company_financial_expense_deductible',
  'company_shl_loan_rate_deductible',
  'company_bank_deductible_rate',
  // Output-only C_FISCALITY_* additions
  'company_loss_carryforward',
  'company_fiscal_depreciation',
  'company_interest_deductible',
  'vat', 'value_added_tax',
  'withholding_tax', 'withholding_tax_dividends',
  'withholding_tax_interest', 'withholding_tax_royalties',
]);

const BANK_ACCOUNTS_TYPES = new Set<string>([
  'company',
  'bank_accounts', 'bank_account',
  'bank_account_funding', 'bank_account_release',
  'time_serie_funding', 'time_serie_release',
]);

/**
 * SECTION_ALLOWLIST — descendant types retained in a section's subtree walk.
 * null means "no filter" (accept every descendant).
 */
export const SECTION_ALLOWLIST: Record<ModelSectionType, Set<string> | null> = {
  [ModelSectionType.COMPANY]: new Set<string>(['company']),
  [ModelSectionType.ASSET]: ASSET_TYPES,
  [ModelSectionType.EQUITY]: EQUITY_TYPES,
  [ModelSectionType.CORPORATE_TAXATION]: CORPORATE_TAXATION_TYPES,
  [ModelSectionType.BANK_ACCOUNTS]: BANK_ACCOUNTS_TYPES,
  [ModelSectionType.FINANCIAL_DEBT]: null,
  [ModelSectionType.INDEXATION]: null,
  [ModelSectionType.MACRO_ECONOMICS]: null,
  [ModelSectionType.TAXATION]: null,
};

function typeOf(item: { type?: string; kind?: string }): string {
  return (item.type || item.kind || '').toLowerCase();
}

/**
 * Returns true if `type` belongs to a section's allowlist. Sections with a
 * null allowlist (FinancialDebt, Indexation, MacroEconomics, Taxation) own
 * their whole subtree — a type matches them only via their root types.
 */
export function typeMatchesSection(type: string, sectionType: ModelSectionType): boolean {
  const allow = SECTION_ALLOWLIST[sectionType];
  const t = type.toLowerCase();
  if (allow === null) {
    return SECTION_ROOT_TYPES[sectionType].includes(t);
  }
  return allow.has(t);
}

/** Returns every section an item belongs to (its type is in the section's allowlist). */
export function categorizeModelData(item: IModelData): ModelSectionType[] {
  const t = typeOf(item);
  if (!t || t === 'input' || t === 'output' || t === 'input-root' || t === 'output-root') {
    return [];
  }
  const result: ModelSectionType[] = [];
  for (const sectionType of Object.values(ModelSectionType)) {
    if (typeMatchesSection(t, sectionType)) result.push(sectionType);
  }
  return result;
}

/**
 * Walks the subtree from roots matching the kind-specific root-type map,
 * keeping descendants whose type is in SECTION_ALLOWLIST[sectionType] (null =
 * all descendants). Returns roots first, then BFS order. Flat IModelData
 * variant.
 *
 * `kind` is the side of the tree we're walking ('Input' / 'Output'). It only
 * matters for the Taxation section today (input root = fiscality, output root
 * = country_fiscality), but threading it through keeps the rest of the
 * system honest.
 */
export function getRecursiveItemsForSection(
  items: IModelData[],
  sectionType: ModelSectionType,
  kind: 'Input' | 'Output' = 'Input'
): IModelData[] {
  const rootTypes = kind === 'Output'
    ? SECTION_OUTPUT_ROOT_TYPES[sectionType]
    : SECTION_INPUT_ROOT_TYPES[sectionType];
  const allow = SECTION_ALLOWLIST[sectionType];

  const byParent = new Map<number | null, IModelData[]>();
  for (const item of items) {
    const pid = (item.parent_id ?? null) as number | null;
    const bucket = byParent.get(pid);
    if (bucket) bucket.push(item);
    else byParent.set(pid, [item]);
  }

  const roots = items.filter(item => rootTypes.includes(typeOf(item)));
  const result: IModelData[] = [];
  const seen = new Set<number>();
  const queue: IModelData[] = [...roots];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (seen.has(current.id)) continue;
    seen.add(current.id);
    result.push(current);

    for (const child of byParent.get(current.id) ?? []) {
      const ct = typeOf(child);
      if (allow !== null && !allow.has(ct)) continue;
      queue.push(child);
    }
  }

  return result;
}

/** Same as getRecursiveItemsForSection but for the TanStack tree items map. */
export function getRecursiveTreeItemsForSection<
  T extends { id: string; type?: string; kind?: string; childrens?: string[] }
>(
  treeItems: Record<string, T>,
  sectionType: ModelSectionType,
  kind: 'Input' | 'Output' = 'Input'
): T[] {
  const rootTypes = kind === 'Output'
    ? SECTION_OUTPUT_ROOT_TYPES[sectionType]
    : SECTION_INPUT_ROOT_TYPES[sectionType];
  const allow = SECTION_ALLOWLIST[sectionType];

  const all = Object.values(treeItems);
  const roots = all.filter(item => rootTypes.includes(typeOf(item)));

  const result: T[] = [];
  const seen = new Set<string>();
  const queue: T[] = [...roots];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (seen.has(current.id)) continue;
    seen.add(current.id);
    result.push(current);

    for (const childId of current.childrens ?? []) {
      const child = treeItems[childId];
      if (!child) continue;
      const ct = typeOf(child);
      if (allow !== null && !allow.has(ct)) continue;
      queue.push(child);
    }
  }

  return result;
}

/**
 * Populates each section by walking its subtree via SECTION_ROOT_TYPES +
 * SECTION_ALLOWLIST. Section order follows the ModelSectionType enum.
 */
export function mapTreeToSections(
  inputItems: IModelData[],
  outputItems: IModelData[]
): ModelSection[] {
  const sections: ModelSection[] = [];
  for (const sectionType of Object.values(ModelSectionType)) {
    const section = createEmptySection(sectionType);
    section.inputItems = getRecursiveItemsForSection(inputItems, sectionType, 'Input');
    section.outputItems = getRecursiveItemsForSection(outputItems, sectionType, 'Output');
    section.itemCount = section.inputItems.length + section.outputItems.length;
    section.hasData = section.itemCount > 0;
    sections.push(section);
  }
  return sections;
}

export function getSectionByType(
  sections: ModelSection[],
  type: ModelSectionType
): ModelSection | undefined {
  return sections.find(section => section.config.id === type);
}

export function getAllItemsFromSection(section: ModelSection): IModelData[] {
  return [...section.inputItems, ...section.outputItems];
}

/** Types allowed in a section — used by AddButtonMenu to filter createable types. */
export function getValidTypesForSection(sectionType: ModelSectionType): string[] {
  const allow = SECTION_ALLOWLIST[sectionType];
  if (allow !== null) return Array.from(allow);
  return [...SECTION_ROOT_TYPES[sectionType]];
}

/** Section summary from the /sections API endpoint. */
export interface SectionSummaryResponse {
  section_id: string;
  has_data: boolean;
  input_count: number;
  output_count: number;
}

/** Builds section cards from the lightweight /sections endpoint response. */
export function buildSectionsFromSummaries(
  summaries: SectionSummaryResponse[]
): ModelSection[] {
  const sections: ModelSection[] = [];
  for (const summary of summaries) {
    const sectionType = summary.section_id as ModelSectionType;
    const config = DEFAULT_SECTION_CONFIGS[sectionType];
    if (!config) continue;
    sections.push({
      config,
      inputItems: [],
      outputItems: [],
      itemCount: summary.input_count + summary.output_count,
      hasData: summary.has_data,
    });
  }
  return sections;
}

export function getMappingStats(sections: ModelSection[]): {
  totalSections: number;
  sectionsWithData: number;
  totalInputItems: number;
  totalOutputItems: number;
  totalItems: number;
  itemsPerSection: Record<string, number>;
} {
  const sectionsWithData = sections.filter(s => s.hasData);
  const totalInputItems = sections.reduce((sum, s) => sum + s.inputItems.length, 0);
  const totalOutputItems = sections.reduce((sum, s) => sum + s.outputItems.length, 0);
  const itemsPerSection: Record<string, number> = {};
  sections.forEach(section => {
    itemsPerSection[section.config.name] = section.itemCount;
  });
  return {
    totalSections: sections.length,
    sectionsWithData: sectionsWithData.length,
    totalInputItems,
    totalOutputItems,
    totalItems: totalInputItems + totalOutputItems,
    itemsPerSection,
  };
}
